import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { TourProvider } from '@reactour/tour';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ToastContainer } from 'react-toastify';

import ThemeContext from '../contexts/themeContext';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import AsideRoutes from '../layout/Aside/AsideRoutes';
import { ToastCloseButton } from '../components/bootstrap/Toasts';
import { Helmet } from 'react-helmet';

import ErrorBoundary from '../ErrorBoundary';

// GLOBAL JS ERROR LOGGER
window.onerror = function (message, source, lineno, colno, error) {
    // Filter out common noise/external errors
    if (typeof message === 'string') {
        if (message.includes('Script error.')) return;
        if (message.includes('Permission denied')) return;
        if (message.includes('ResizeObserver loop')) return;
    }
    if (source && typeof source === 'string' && (source.includes('clarity') || source.includes('google'))) return;

    fetch("https://panel.lojistigo.com/api/log-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            type: "windowError",
            message,
            source,
            lineno,
            colno,
            stack: error?.stack,
            userAgent: navigator.userAgent,
            url: window.location.href,
            time: new Date().toISOString(),
        }),
    }).catch(() => { });
};

window.addEventListener("unhandledrejection", (event) => {
    fetch("https://panel.lojistigo.com/api/log-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            type: "promiseError",
            message: event.reason?.message ?? event.reason,
            stack: event.reason?.stack,
            userAgent: navigator.userAgent,
            url: window.location.href,
            time: new Date().toISOString(),
        }),
    }).catch(() => { });
});

const App = () => {
    getOS();

    dayjs.extend(localizedFormat);
    dayjs.extend(relativeTime);

    const { themeStatus, darkModeStatus } = useDarkMode();
    const theme = {
        theme: themeStatus,
        primary: COLORS.PRIMARY.code,
        secondary: COLORS.SECONDARY.code,
        success: COLORS.SUCCESS.code,
        info: COLORS.INFO.code,
        warning: COLORS.WARNING.code,
        danger: COLORS.DANGER.code,
        dark: COLORS.DARK.code,
        light: COLORS.LIGHT.code,
    };

    useEffect(() => {
        if (darkModeStatus) {
            document.documentElement.setAttribute('theme', 'dark');
            document.documentElement.setAttribute('data-bs-theme', 'dark');
        }
        return () => {
            document.documentElement.removeAttribute('theme');
            document.documentElement.removeAttribute('data-bs-theme');
        };
    }, [darkModeStatus]);

    const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
    const ref = useRef(null);

    useFullscreen(ref, fullScreenStatus, {
        onClose: () => setFullScreenStatus(false),
    });

    useLayoutEffect(() => {
        if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
            document.body.classList.add('modern-design');
        } else {
            document.body.classList.remove('modern-design');
        }
    });

    return (
        <ErrorBoundary>
            <ThemeProvider theme={theme}>
                <Helmet>
                    <script async src="https://www.googletagmanager.com/gtag/js?id=G-KDLW6WHL17"></script>
                    <script>
                        {`
                            window.dataLayer = window.dataLayer || [];
                            function gtag(){dataLayer.push(arguments);}
                            gtag('js', new Date());
                            gtag('config', 'G-KDLW6WHL17');
                        `}
                    </script>

                    {/* MICROSOFT CLARITY */}
                    <script type="text/javascript">
                        {`
                            (function(c,l,a,r,i,t,y){
                                c[a] = c[a] || function(){ (c[a].q = c[a].q || []).push(arguments) };
                                t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
                                y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t,y);
                            })(window, document, "clarity", "script", "sldl7y1p94");
                        `}
                    </script>
                </Helmet>

                <TourProvider steps={steps} styles={styles} showNavigation={false} showBadge={false}>
                    <div
                        ref={ref}
                        className='app'
                        style={{
                            backgroundColor: fullScreenStatus ? 'var(--bs-body-bg)' : undefined,
                            zIndex: fullScreenStatus ? 1 : undefined,
                            overflow: fullScreenStatus ? 'scroll' : undefined,
                        }}
                    >
                        <AsideRoutes />
                        <Wrapper />
                    </div>

                    <Portal id="portal-notification">
                        <ReactNotifications />
                    </Portal>

                    <ToastContainer
                        closeButton={ToastCloseButton}
                        toastClassName="toast show"
                    />
                </TourProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default App;
